import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { clampPercentage, formatPercentage } from '../../../core/utils/common/formatters';
import { RankingItem } from '../../../core/types/common.types';
import MonitorHeartOutlined from '@mui/icons-material/MonitorHeartOutlined';
import MedicalServicesOutlined from '@mui/icons-material/MedicalServicesOutlined';
import BloodtypeOutlined from '@mui/icons-material/BloodtypeOutlined';
import CoronavirusOutlined from '@mui/icons-material/CoronavirusOutlined';
import VaccinesOutlined from '@mui/icons-material/VaccinesOutlined';
import AirOutlined from '@mui/icons-material/AirOutlined';
import MonitorWeightOutlined from '@mui/icons-material/MonitorWeightOutlined';

function getDisorderIcon(label: string) {
  const normalisedLabel = label.toLowerCase();

  if (normalisedLabel.includes('hypertension') || normalisedLabel.includes('blood pressure')) {
    return MonitorHeartOutlined;
  }

  if (
    normalisedLabel.includes('retrovirus') ||
    normalisedLabel.includes('hiv') ||
    normalisedLabel.includes('infection') ||
    normalisedLabel.includes('infections')
  ) {
    return CoronavirusOutlined;
  }

  if (
    normalisedLabel.includes('hyperlipidaemia') ||
    normalisedLabel.includes('hyperlipidemia') ||
    normalisedLabel.includes('lipid') ||
    normalisedLabel.includes('cholesterol') ||
    normalisedLabel.includes('blood fats')
  ) {
    return BloodtypeOutlined;
  }

  if (normalisedLabel.includes('diabetes') || normalisedLabel.includes('diabetes mellitus')) {
    return VaccinesOutlined;
  }

  if (
    normalisedLabel.includes('dyspnoea') ||
    normalisedLabel.includes('dyspnea') ||
    normalisedLabel.includes('shortness of breath') ||
    normalisedLabel.includes('breath')
  ) {
    return AirOutlined;
  }

  if (normalisedLabel.includes('obesity') || normalisedLabel.includes('weight')) {
    return MonitorWeightOutlined;
  }

  return MedicalServicesOutlined;
}

interface DonutChartProps {
  item: RankingItem;
  color: string;
  backgroundColor: string;
}

function DonutChart({ item, color, backgroundColor }: DonutChartProps) {
  const percentage = clampPercentage(item.value);
  const degrees = percentage * 3.6;
  const DisorderIcon = getDisorderIcon(item.label);

  return (
    <Tooltip title={`${item.label}: ${formatPercentage(item.value)} of participants`}>
      <Paper
        elevation={0}
        sx={{
          height: '100%',
          p: 2,
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h3"
          component="p"
          sx={{
            fontWeight: 900,
            lineHeight: 1,
            color,
            mb: 1.5,
          }}
        >
          {formatPercentage(item.value)}
        </Typography>

        <Box
          sx={{
            width: 132,
            height: 132,
            borderRadius: '50%',
            mb: 1.5,
            background: `conic-gradient(${color} 0deg ${degrees}deg, ${backgroundColor} ${degrees}deg 360deg)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `inset 0 0 0 1px ${backgroundColor}`,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: 86,
              height: 86,
              borderRadius: '50%',
              bgcolor: 'background.paper',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 1,
            }}
          >
            <DisorderIcon sx={{ fontSize: 44, color }} />
          </Box>
        </Box>

        <Typography
          variant="body2"
          component="p"
          sx={{
            fontWeight: 800,
            color: 'text.primary',
            maxWidth: 190,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {item.label}
        </Typography>
      </Paper>
    </Tooltip>
  );
}

export default DonutChart;
